import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import * as Icons from 'lucide-react';
import { RefreshCw } from 'lucide-react';

const StatCardDisplay = ({ component, customization = {} }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [component.ComponentID]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${component.ComponentID}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load data');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Error loading stat card data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const config = typeof component.ConfigJSON === 'string' 
    ? JSON.parse(component.ConfigJSON) 
    : component.ConfigJSON || component.config || {};

  // Apply customization overrides
  const effectiveConfig = {
    ...config,
    ...customization
  };

  const IconComponent = Icons[effectiveConfig.icon || 'Users'] || Icons.Users;
  
  const colorClasses = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', lightBg: 'bg-blue-50 dark:bg-blue-900/20' },
    green: { bg: 'bg-green-500', text: 'text-green-600', lightBg: 'bg-green-50 dark:bg-green-900/20' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-600', lightBg: 'bg-purple-50 dark:bg-purple-900/20' },
    red: { bg: 'bg-red-500', text: 'text-red-600', lightBg: 'bg-red-50 dark:bg-red-900/20' },
    yellow: { bg: 'bg-yellow-500', text: 'text-yellow-600', lightBg: 'bg-yellow-50 dark:bg-yellow-900/20' },
    indigo: { bg: 'bg-indigo-500', text: 'text-indigo-600', lightBg: 'bg-indigo-50 dark:bg-indigo-900/20' }
  };

  const colors = colorClasses[effectiveConfig.color || 'blue'] || colorClasses.blue;

  const formatValue = (value) => {
    if (!value && value !== 0) return '0';

    switch (effectiveConfig.format || 'number') {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'decimal':
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US').format(value);
    }
  };

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-red-200 dark:border-red-800">
        <div className="text-red-600 dark:text-red-400 text-sm">
          Error loading data
        </div>
      </div>
    );
  }

  // Function to handle drilldown navigation
  const handleDrilldown = () => {
    if (effectiveConfig.drilldownUrl) {
      // Check if URL is absolute or relative
      if (effectiveConfig.drilldownUrl.startsWith('http')) {
        window.open(effectiveConfig.drilldownUrl, '_blank');
      } else {
        // Use React Router or window.location for internal navigation
        window.location.href = effectiveConfig.drilldownUrl;
      }
    }
  };

  return (
    <div 
      className={`${colors.lightBg} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700 h-full flex flex-col justify-center ${effectiveConfig.drilldownUrl ? 'cursor-pointer' : ''}`}
      onClick={handleDrilldown}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {customization.label || effectiveConfig.label || component.Name}
          </p>
          {loading ? (
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
              <span className="text-gray-400">Loading...</span>
            </div>
          ) : (
            <p className={`text-3xl font-bold ${colors.text}`}>
              {formatValue(data?.value)}
            </p>
          )}
        </div>
        <div className={`p-3 ${colors.bg} rounded-lg text-white flex-shrink-0`}>
          <IconComponent className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};

export default StatCardDisplay;

